// VideoTile.js
import React, { useState, useEffect, useRef } from 'react';
import './VideoTile.css';
import WaveSurfer from 'wavesurfer.js';
import thumbnail from './assets/image/alekhine_thumbnail.jpg'; // Import the thumbnail image

const VideoTile = ({
  video,
  title,
  duration,
  summary,
  additionalInfo,
  onRegenerateVideo,
  onRegenerateVideoPostEdit,
  onRegenerateShot,
  onSelectShotVideo,
  loading,
  isMerging, // NEW
  // Controlled Props from NarrativeWorkbench
  showHistory = false,
  showSlideshow = false,
  activeMode = 'video',
  onSlideChange, // New prop to report active shot ID
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [visualPrompt, setVisualPrompt] = useState(additionalInfo.visual);
  const [sceneVideo, setSceneVideo] = useState(video);
  const [audioUrls, setAudioUrls] = useState([]);
  const [isPlaying, setIsPlaying] = useState([]);
  const waveSurferInstances = useRef([]);

  // NEW: Internal slide index controls
  const [slideIndex, setSlideIndex] = useState(0);

  // Function to sanitize the title for a valid CSS selector
  const sanitizeTitle = (title) => {
    return title.replace(/[^a-zA-Z0-9]/g, '-');
  };

  useEffect(() => {
    setSceneVideo(video);
  }, [video]);

  useEffect(() => {
    const urls = additionalInfo.dialogues.map(dialogue => dialogue.audio);
    setAudioUrls(urls);
  }, [additionalInfo.dialogues]);

  // Compute slides from shotMatrix
  const slides = React.useMemo(() => {
    const matrix = Array.isArray(additionalInfo?.shotMatrix) ? additionalInfo.shotMatrix : [];
    const shotVideos = [];
    matrix
      .slice()
      .sort((a, b) => (a?.beat ?? 0) - (b?.beat ?? 0))
      .forEach(row => {
        const shots = Array.isArray(row?.shots) ? row.shots : [];
        shots
          .slice()
          .sort((a, b) => String(a?.id || '').localeCompare(String(b?.id || '')))
          .forEach(sh => {
            if (sh?.videoUrl) {
              const label = `Beat ${row?.beat ?? ''} • Shot ${sh?.id ?? ''}`;
              shotVideos.push({ url: sh.videoUrl, label, shotId: sh.id, beat: row.beat, videoHistory: sh.videoHistory || [] });
            }
          });
      });
    return shotVideos;
  }, [additionalInfo?.shotMatrix]);

  // Report active shot ID to parent whenever slide changes
  useEffect(() => {
    if (showSlideshow && slides[slideIndex]) {
      onSlideChange?.(slides[slideIndex].shotId);
    }
  }, [slideIndex, slides, showSlideshow, onSlideChange]);

  const hasSlides = slides.length > 0;

  // If externally switched OFF slideshow, no effect needed here as we use prop
  // If we wanted to auto-disable slideshow if no slides exist, parent would need to know
  // but simpler to just strictly respect prop.

  const goPrev = (e) => { e?.stopPropagation?.(); setSlideIndex(i => Math.max(0, i - 1)); };
  const goNext = (e) => { e?.stopPropagation?.(); setSlideIndex(i => Math.min((slides.length - 1), i + 1)); };
  const atStart = slideIndex <= 0;
  const atEnd = slideIndex >= Math.max(0, slides.length - 1);


  const handleToggleDetails = () => {
    setShowDetails(!showDetails);
  };

  // Logic adapted to use prop
  const handleRegenerateVideo = async () => {
    if (showSlideshow && slides[slideIndex]) {
      // Regenerate specific shot
      const currentSlide = slides[slideIndex];
      // Pass visual prompt if needed, or null to let parent decide
      onRegenerateShot(currentSlide.shotId, visualPrompt);
    } else {
      // Regenerate full merged/scene video
      onRegenerateVideo(visualPrompt);
    }
  };

  const handleVisualPromptClick = () => {
    setIsEditing(true);
  };

  const handleVisualPromptChange = (e) => {
    setVisualPrompt(e.target.value);
  };

  const handleVisualPromptKeyDown = async (e) => {
    if (e.key === 'Enter') {
      setIsEditing(false);
      if (showSlideshow && slides[slideIndex]) {
        onRegenerateShot(slides[slideIndex].shotId, visualPrompt);
      } else {
        onRegenerateVideoPostEdit(visualPrompt);
      }
    }
  };

  const togglePlayPause = (index) => {
    // ... code kept for future audio use ...
  };

  const currentVideoSrc = showSlideshow ? slides[slideIndex]?.url : sceneVideo;
  const currentTitle = showSlideshow
    ? slides[slideIndex]?.label
      ? `${title} — ${slides[slideIndex].label} (${slideIndex + 1}/${slides.length})`
      : title
    : title;

  const currentSlideHasHistory = showSlideshow && slides[slideIndex]?.videoHistory?.length > 0;


  return (
    <div className="video-tile">
      <div className="title-bar">
        <h3 title={currentTitle}>{currentTitle}</h3>
        <div className="title-actions">
          {/* All actions moved to Vertical Toolbar in NarrativeWorkbench */}
          {/* We keep this empty container or remove it if CSS allows, kept for Flex spacing consistency if needed */}
        </div>
      </div>
      <div className="video-wrapper">
        {currentVideoSrc && (
          <video
            key={currentVideoSrc} // Force remount on source change to ensure proper loading
            src={currentVideoSrc}
            controls={!loading} // Disable controls while loading
            className={`scene-video${loading ? ' blur-effect' : ''}`}
          />
        )}

        {showSlideshow && slides.length > 1 && !loading && (
          <>
            <button
              className={`nav-arrow nav-left${atStart ? ' disabled' : ''}`}
              onClick={goPrev}
              aria-label="Previous video"
            >
              ‹
            </button>
            <button
              className={`nav-arrow nav-right${atEnd ? ' disabled' : ''}`}
              onClick={goNext}
              aria-label="Next video"
            >
              ›
            </button>
          </>
        )}

        {loading && (
          <div className="video-loading-overlay">
            <img src={`${process.env.PUBLIC_URL}/static/media/loading1.eb6327c2b020fd151d29.gif`} alt="Loading..." className="loading-animation" />
            <div>Generating video...</div>
          </div>
        )}

        {isMerging && !showSlideshow && !loading && (
          <div className="video-loading-overlay" style={{ background: 'rgba(0,0,0,0.6)' }}>
            <img src={`${process.env.PUBLIC_URL}/static/media/loading1.eb6327c2b020fd151d29.gif`} alt="Merging..." className="loading-animation" />
            <div>Merging scene...</div>
          </div>
        )}
      </div>

      {/* History Strip */}
      {
        showHistory && currentSlideHasHistory && showSlideshow && (
          <div className="history-strip video-history-strip">
            {slides[slideIndex].videoHistory.map((url, i) => (
              <div key={i} className={`history-thumb-wrapper ${url === currentVideoSrc ? 'active' : ''}`} onClick={() => onSelectShotVideo(slides[slideIndex].shotId, url)}>
                <video
                  src={url}
                  className="history-thumb-video"
                  muted
                  onMouseOver={e => e.target.play()}
                  onMouseOut={e => { e.target.pause(); e.target.currentTime = 0; }}
                />
              </div>
            ))}
          </div>
        )
      }
    </div>
  );
};

export default VideoTile;
